import type { WorkspaceResponseV2 } from '@emma/api_client/types';
import { Box, Dropdown, Flex, Spinner, Tooltip } from '@lego/klik-ui';
import { useEffect, useState } from 'react';
import { useSWRConfig } from 'swr';
import { EditButtons } from '@/components/common/EditButtons';
import { useReplaceWorkspace, useWorkspaceAg2Applications } from '@/hooks/workspace';

type Props = {
  workspace: Pick<
    WorkspaceResponseV2,
    'id' | 'enterpriseArchitectureProductId' | 'description' | 'ag2AppIds'
  >;
  isLoading?: boolean;
};

export const ApplicationIdSelect = ({ workspace, isLoading = false }: Props) => {
  const [selectedIds, setSelectedIds] = useState<string[]>(workspace.ag2AppIds);
  const [isEditing, setIsEditing] = useState(false);
  const [pendingIds, setPendingIds] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const { mutate } = useSWRConfig();
  const { data, isLoading: isApplicationsLoading } = useWorkspaceAg2Applications(
    workspace.enterpriseArchitectureProductId
  );
  const replaceWorkspace = useReplaceWorkspace(workspace.id);
  const cacheKey = `workspaces/${workspace.id}`;
  const isDisabled = isLoading || isApplicationsLoading || isSaving;
  const isReadOnlyLoading = selectedIds.length === 0 && (isLoading || isApplicationsLoading);

  useEffect(() => {
    setSelectedIds(workspace.ag2AppIds);
  }, [workspace.ag2AppIds]);

  const displayText = selectedIds.length > 0 ? selectedIds.join(', ') : 'None';
  const applicationOptions =
    data?.applications
      .map((application) => ({
        label: `${application.name} (${application.id})`,
        value: application.id,
      }))
      .sort((a, b) => a.value.localeCompare(b.value)) ?? [];

  const handleStartEdit = () => {
    setPendingIds([...selectedIds]);
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!workspace.enterpriseArchitectureProductId) {
      return;
    }

    setIsSaving(true);

    try {
      const updatedWorkspace = await replaceWorkspace({
        enterpriseArchitectureProductId: workspace.enterpriseArchitectureProductId,
        description: workspace.description,
        ag2AppIds: pendingIds,
      });

      setSelectedIds(updatedWorkspace.ag2AppIds);
      await mutate(cacheKey, updatedWorkspace, { revalidate: false });
      setIsEditing(false);
    } catch {
      // Already toasted and logged by the mutation hook; caught only to
      // avoid an unhandled promise rejection here.
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setPendingIds([...selectedIds]);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <Flex align="center" gap={2}>
        <Dropdown
          isDisabled={isDisabled || !workspace.enterpriseArchitectureProductId}
          isMultiple
          onChange={(values: string[]) => {
            setPendingIds(values);
          }}
          placeholder={
            !workspace.enterpriseArchitectureProductId
              ? 'Missing product ID'
              : isLoading || isApplicationsLoading
                ? 'Loading...'
                : 'Select Application IDs'
          }
          shouldDisplaySelectionInline={false}
          shouldDisplaySelectionLabels
          size="sm"
          value={pendingIds}
          width="260px"
        >
          {[...applicationOptions]
            .sort((a, b) => {
              const aSelected = pendingIds.includes(a.value) ? 0 : 1;
              const bSelected = pendingIds.includes(b.value) ? 0 : 1;
              return aSelected - bSelected;
            })
            .map((application) => (
              <Dropdown.Option key={application.value} value={application.value}>
                {application.label}
              </Dropdown.Option>
            ))}
        </Dropdown>
        <EditButtons
          isEditing={isEditing}
          isSaving={isSaving}
          onCancel={handleCancel}
          onSave={() => {
            void handleSave();
          }}
          saveDisabled={isDisabled || !workspace.enterpriseArchitectureProductId}
        />
      </Flex>
    );
  }

  return (
    <Flex align="center" gap="2">
      <Tooltip label={displayText} placement="top">
        <Box
          as="span"
          display="inline-block"
          maxWidth="200px"
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          {isReadOnlyLoading ? <Spinner height={8} size="sm" width={8} /> : displayText}
        </Box>
      </Tooltip>
      <EditButtons
        editDisabled={isLoading || !workspace.enterpriseArchitectureProductId}
        editLabel="Edit Application IDs"
        isEditing={false}
        isSaving={false}
        onCancel={() => {}}
        onEdit={handleStartEdit}
        onSave={() => {}}
      />
    </Flex>
  );
};
